import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import { collection, doc, getDoc, getDocs, query, where } from "firebase/firestore";
import { auth, db } from "../../firebase";
import Window from "../../components/Window";
import Icon, { IconTile } from "../../components/Icon";
import { getUsername } from "../../penpal";
import { CIRCLE_LIMITS, Circle, circleRef, requestCircle } from "../../circles";
import "../../styles.css";

// Private Circles: the ones you're in, the ones you can ask to join, and a
// form to start a new one (you'd be its quartermaster).
export default function Circles() {
  const [user] = useAuthState(auth);
  const navigate = useNavigate();
  const [open, setOpen] = useState<Circle[] | null>(null);
  const [mine, setMine] = useState<Circle[]>([]);
  const [username, setUsername] = useState("");

  useEffect(() => {
    getDocs(query(collection(db, "circles"), where("status", "==", "approved")))
      .then((snap) =>
        setOpen(
          snap.docs
            .map((d) => ({ id: d.id, ...(d.data() as Omit<Circle, "id">) }))
            .sort((a, b) => a.name.localeCompare(b.name))
        )
      )
      .catch(() => setOpen([]));
  }, []);

  useEffect(() => {
    if (!user) return;
    getUsername(user.uid).then(setUsername).catch(() => setUsername("unknown"));
    // circles you lead (including ones waiting for approval) and belong to
    (async () => {
      const ids = new Set<string>();
      const led = await getDocs(query(collection(db, "circles"), where("quartermaster", "==", user.uid))).catch(
        () => null
      );
      led?.docs.forEach((d) => ids.add(d.id));
      const priv = await getDoc(doc(db, "users", user.uid, "private", "rooms")).catch(() => null);
      ((priv?.data()?.circles as string[]) || []).forEach((id) => ids.add(id));
      const circles = await Promise.all(
        Array.from(ids).map((id) =>
          getDoc(circleRef(id))
            .then((s) => (s.exists() ? ({ id: s.id, ...s.data() } as Circle) : null))
            .catch(() => null)
        )
      );
      setMine(circles.filter((c): c is Circle => !!c));
    })();
  }, [user]);

  const mineIds = new Set(mine.map((c) => c.id));
  const others = (open || []).filter((c) => !mineIds.has(c.id));

  return (
    <>
      <Window title="private_circles.htm" tone="lavender">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="window-heading flex items-center gap-3">
              <IconTile name="lock" tone="violet" size={44} /> Private Circles
            </h1>
            <p className="window-sub !mb-0">
              Small, members-only spaces for folks navigating similar things. Each
              circle is led by a quartermaster, who welcomes new members and keeps
              it kind. Posts are only seen by members.
            </p>
          </div>
          <Link to="/community" className="btn btn-plain btn-sm">
            <Icon name="back" size={16} /> All rooms
          </Link>
        </div>
      </Window>

      {mine.length > 0 && (
        <Window title={`my_circles (${mine.length})`} tone="mint">
          <div className="grid gap-4 sm:grid-cols-2">
            {mine.map((c) => (
              <CircleCard key={c.id} c={c} you={user?.uid} />
            ))}
          </div>
        </Window>
      )}

      <Window title="circles_to_join.txt" tone="tan">
        {open === null ? (
          <p className="empty">Looking for circles...</p>
        ) : others.length === 0 ? (
          <p className="text-sm text-ink-soft">
            {open.length === 0
              ? "No circles have opened yet. Want to start the first one?"
              : "You're in every open circle! Start a new one below."}
          </p>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            {others.map((c) => (
              <CircleCard key={c.id} c={c} you={user?.uid} />
            ))}
          </div>
        )}
      </Window>

      {user && (
        <Window title="start_a_circle.exe" tone="blue">
          <details>
            <summary className="btn btn-blue cursor-pointer list-none">
              <Icon name="edit" size={16} /> Request a new circle
            </summary>
            {username === "unknown" ? (
              <p className="notice mt-4 text-sm">
                Choose a username on <Link to="/profile">My Profile</Link> first.
              </p>
            ) : (
              <RequestForm
                onSubmit={async (name, purpose, guidelines) => {
                  const id = await requestCircle(user.uid, username, name, purpose, guidelines);
                  navigate(`/circles/${id}`);
                }}
              />
            )}
          </details>
        </Window>
      )}
    </>
  );
}

function CircleCard({ c, you }: { c: Circle; you?: string }) {
  const role = c.quartermaster === you ? "You're the quartermaster" : c.mates?.includes(you || "") ? "You're a first mate" : "";
  return (
    <Link to={`/circles/${c.id}`} className="tile bg-paper">
      <h2 className="tile-title !mt-0">{c.name}</h2>
      <p className="text-sm">{c.purpose.length > 160 ? c.purpose.slice(0, 160) + "…" : c.purpose}</p>
      <p className="mt-2 text-xs text-ink-soft">
        Quartermaster: @{c.quartermasterName}
        {role && ` · ${role}`}
      </p>
      {c.status === "pending" && <span className="chip mt-2">waiting for approval</span>}
      {c.status === "paused" && <span className="chip mt-2">paused</span>}
    </Link>
  );
}

function RequestForm({ onSubmit }: { onSubmit: (name: string, purpose: string, guidelines: string) => Promise<void> }) {
  const [name, setName] = useState("");
  const [purpose, setPurpose] = useState("");
  const [guidelines, setGuidelines] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!name.trim() || !purpose.trim()) {
      setError("Please give the circle a name and say what it's for.");
      return;
    }
    setBusy(true);
    try {
      await onSubmit(name, purpose, guidelines);
    } catch (err) {
      console.error(err);
      setError(
        auth.currentUser?.emailVerified
          ? "Sorry, that didn't go through. Please try again."
          : "Please verify your email first. Use the reminder at the top of the page to resend the link."
      );
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-4 flex flex-col gap-4">
      <p className="text-sm">
        You'll be the circle's <strong>quartermaster</strong>: you welcome new
        members, keep things kind, and can name up to two first mates to help.
        You can pass the role on anytime. Our moderators look over each new
        circle before it opens.
      </p>
      <label className="field">
        <span className="field-label">Circle name</span>
        <input
          className="input"
          value={name}
          maxLength={CIRCLE_LIMITS.name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Sober & Sparkly, QTPOC Tea Time, Transition Buddies"
        />
      </label>
      <label className="field">
        <span className="field-label">Who it's for and what it's about</span>
        <textarea
          className="input"
          value={purpose}
          maxLength={CIRCLE_LIMITS.purpose}
          onChange={(e) => setPurpose(e.target.value)}
        />
      </label>
      <label className="field">
        <span className="field-label">Circle guidelines (optional)</span>
        <textarea
          className="input"
          value={guidelines}
          maxLength={CIRCLE_LIMITS.guidelines}
          onChange={(e) => setGuidelines(e.target.value)}
          placeholder="e.g. What's shared here stays here. No unsolicited advice. Use content warnings."
        />
        <span className="text-xs text-ink-soft">On top of the site's community guidelines.</span>
      </label>
      {error && (
        <p className="error-text" role="alert">
          {error}
        </p>
      )}
      <div>
        <button type="submit" className="btn" disabled={busy}>
          <Icon name="send" size={16} /> {busy ? "Sending..." : "Send my request"}
        </button>
      </div>
    </form>
  );
}
